// ===== MeatMen.js =====
// src/components/Games/MeatMen.js
import React, { useState, useEffect, useRef, useCallback } from 'react';

// ============================================
// GAME CONSTANTS
// ============================================

const BODY_PARTS = {
  left_arm: { name: 'LEFT ARM', organic: true, hp: 100 },
  right_arm: { name: 'RIGHT ARM', organic: true, hp: 100 },
  left_leg: { name: 'LEFT LEG', organic: true, hp: 100 },
  right_leg: { name: 'RIGHT LEG', organic: true, hp: 100 },
  torso: { name: 'TORSO', organic: true, hp: 100 },
  head: { name: 'HEAD', organic: true, hp: 100 },
  lungs: { name: 'LUNGS', organic: true, hp: 100 },
  heart: { name: 'HEART', organic: true, hp: 100 },
  eyes: { name: 'EYES', organic: true, hp: 100 },
};

const PROSTHETIC_NAMES = {
  left_arm: 'SYNTHETIC ARM (L)',
  right_arm: 'SYNTHETIC ARM (R)',
  left_leg: 'HYDRAULIC LEG (L)',
  right_leg: 'HYDRAULIC LEG (R)',
  torso: 'REINFORCED CHASSIS',
  head: 'CRANIAL PLATE',
  lungs: 'FILTRATION UNIT',
  heart: 'PUMP MODULE',
  eyes: 'OPTICAL ARRAY',
};

const WEAPONS = {
  fists: { name: 'FISTS', damage: [5, 15], accuracy: 85, ammo: Infinity, description: 'Your hands. Or what\'s left of them.' },
  combat_knife: { name: 'COMBAT KNIFE', damage: [10, 25], accuracy: 80, ammo: Infinity, description: 'Standard issue. The blade is dull from use.' },
  pistol: { name: 'SIDEARM', damage: [15, 35], accuracy: 70, ammo: 12, maxAmmo: 12, description: 'A .45 caliber pistol. Reliable but weak.' },
  rifle: { name: 'ASSAULT RIFLE', damage: [25, 50], accuracy: 65, ammo: 30, maxAmmo: 30, description: 'Kinetic rifle. Effective against light armor.' },
  shotgun: { name: 'SHOTGUN', damage: [35, 70], accuracy: 55, ammo: 8, maxAmmo: 8, description: 'Devastating at close range.' },
  grenade: { name: 'FRAG GRENADE', damage: [50, 90], accuracy: 75, ammo: 2, maxAmmo: 4, description: 'Pull pin. Throw. Don\'t think about it.' },
};

const PILLS = {
  painkillers: { name: 'PAINKILLERS', effect: 'Restores 20 HP to all body parts', icon: '💊' },
  stims: { name: 'COMBAT STIMS', effect: '+25% damage for 3 turns', icon: '💉' },
  brain_pills: { name: 'NEURAL STABILIZERS', effect: 'Reduces war flash chance by 50%', icon: '🧠' },
  organ_pills: { name: 'ORGAN REGULATORS', effect: 'Prevents prosthetic rejection for 5 turns', icon: '⚙️' },
};

const ENEMIES = [
  {
    id: 'martian_conscript',
    name: 'MARTIAN CONSCRIPT',
    hp: [40, 70],
    damage: [5, 15],
    accuracy: 50,
    xp: 10,
    description: 'A young Martian soldier. He looks as scared as you feel.',
    deathMessages: [
      'He falls. He was barely older than you.',
      'He reaches for something in his pocket as he dies. A photograph. You don\'t look at it.',
      'He whispers something in Martian. You don\'t understand. You don\'t want to.',
    ],
  },
  {
    id: 'martian_soldier',
    name: 'MARTIAN SOLDIER',
    hp: [60, 100],
    damage: [10, 25],
    accuracy: 60,
    xp: 20,
    description: 'A veteran fighter. His armor is scarred from previous battles.',
    deathMessages: [
      'He dies on his feet, still swinging.',
      'He spits blood at you before collapsing. You can\'t blame him.',
      'His body hits the red dust. Mars claims another one.',
    ],
  },
  {
    id: 'martian_heavy',
    name: 'MARTIAN HEAVY TROOPER',
    hp: [100, 150],
    damage: [20, 40],
    accuracy: 55,
    xp: 35,
    description: 'Armored and armed with a plasma cannon. The ground shakes when he walks.',
    deathMessages: [
      'The armor cracks. The man inside was smaller than you expected.',
      'His plasma cannon overloads as he falls. The explosion blinds you for a moment.',
      'He goes down hard. The ground trembles one last time.',
    ],
  },
  {
    id: 'martian_sniper',
    name: 'MARTIAN SNIPER',
    hp: [35, 55],
    damage: [30, 55],
    accuracy: 75,
    xp: 30,
    description: 'You can\'t see her. But she can see you.',
    deathMessages: [
      'You find her position after she stops shooting. She bled out alone.',
      'Her rifle is better than yours. You take it. You try not to think about it.',
      'She was hiding in the rocks. She almost got you.',
    ],
  },
  {
    id: 'martian_berserker',
    name: 'MARTIAN BERSERKER',
    hp: [80, 120],
    damage: [25, 50],
    accuracy: 70,
    xp: 40,
    description: 'Screaming. Charging. No armor. No fear. Just rage.',
    deathMessages: [
      'Even after he stops moving, his muscles keep twitching.',
      'He smiled when he died. You don\'t know why. You don\'t want to know.',
      'The screaming stops. The silence is worse.',
    ],
  },
  {
    id: 'drone_swarm',
    name: 'RECON DRONE SWARM',
    hp: [25, 40],
    damage: [15, 30],
    accuracy: 80,
    xp: 15,
    description: 'A cloud of small attack drones. They move like insects.',
    deathMessages: [
      'The drones fall like dead flies. Their lights blink out one by one.',
      'You crush the last one under your boot. It sparks and dies.',
      'The swarm disperses. Broken drones litter the ground like shrapnel.',
    ],
  },
];

const LOCATIONS = [
  {
    name: 'OLYMPUS MONS TRENCHLINE',
    description: 'Red dust fills the trenches carved into the base of the largest volcano in the solar system. The walls are stained with blood — human and Martian.',
    enemyTypes: ['martian_conscript', 'martian_soldier'],
    lootChance: 0.4,
  },
  {
    name: 'VALLES MARINERIS — SECTOR 7',
    description: 'The canyon stretches endlessly. Somewhere below, the Martians have dug tunnels. You can hear them moving under your feet.',
    enemyTypes: ['martian_soldier', 'martian_sniper'],
    lootChance: 0.5,
  },
  {
    name: 'THARSIS PLATEAU — FORWARD BASE ECHO',
    description: 'What\'s left of Forward Base Echo. The Engineers abandoned it months ago. Now it\'s a killing field.',
    enemyTypes: ['martian_heavy', 'martian_soldier', 'drone_swarm'],
    lootChance: 0.6,
  },
  {
    name: 'HELLAS BASIN — THE BONEYARD',
    description: 'They call it the Boneyard because that\'s what it is. Wrecked vehicles, broken drones, and bodies. So many bodies.',
    enemyTypes: ['martian_berserker', 'martian_conscript', 'martian_heavy'],
    lootChance: 0.5,
  },
  {
    name: 'ARCADIA PLANITIA — NO MAN\'S LAND',
    description: 'Flat, open, nowhere to hide. The Martians know this. They use it as a kill zone. The Engineers send us through it anyway.',
    enemyTypes: ['martian_sniper', 'martian_soldier', 'drone_swarm'],
    lootChance: 0.3,
  },
  {
    name: 'UNDERGROUND TUNNEL NETWORK',
    description: 'Dark. Tight. The walls are carved Martian stone. You can barely stand. Every shadow could be an ambush.',
    enemyTypes: ['martian_berserker', 'martian_conscript'],
    lootChance: 0.45,
  },
];

const WAR_FLASH_MESSAGES = [
  'Static fills your vision. When it clears, your hands are covered in blood. You don\'t know whose.',
  'Your eyes fill with red. You hear screaming — yours or theirs, you can\'t tell anymore.',
  'Something switches off in your brain. Something else switches on. You are not Manu anymore.',
  'The rage hits like a wave. Your muscles move on their own. You are a passenger in your own body.',
  'Your vision narrows to a tunnel. Everything is a target. Everything must die.',
  'You feel the wires in your head spark. The adrenaline dumps into your bloodstream. You can\'t stop it.',
];

const FIELD_MEDIC_MESSAGES = [
  'The medic drone hovers over you. Cold metal hands press against your wounds. "Hold still, soldier."',
  'You feel the needle go in. The pain fades. Everything fades. When you wake up, something has been replaced.',
  'The field medic patches you together. You don\'t ask what they used. You don\'t want to know.',
  '"You\'re lucky," the medic says. You don\'t feel lucky.',
  'They drag you back to the med tent. The doctor looks at you and sighs. "Another one."',
];

const BETWEEN_MISSION_MESSAGES = [
  'You take your pills. Pills for the pain, pills for your brain, pills to keep your body from going insane.',
  'You sit in the barracks and stare at the wall. Someone is crying in the next bunk. You don\'t look.',
  'The Engineers walk past without looking at you. You are equipment. Equipment doesn\'t need eye contact.',
  'You eat something grey and tasteless. Your synthetic stomach processes it. You feel nothing.',
  'Another day on Mars. Another day of not dying. That\'s all you can hope for.',
  'You dream of home. You dream of your mother. When you wake up, you can\'t remember her face.',
  'The soldier next to you didn\'t come back from today\'s mission. His bunk is already being cleaned out.',
  'You check your body. Count the scars. Count the prosthetics. Count what\'s left of you.',
];

const MEMORY_FRAGMENTS = [
  'You see a flash: your mother\'s face. Then it\'s gone.',
  'For a moment, you remember a name. Dmitri. Then it dissolves.',
  'You taste bean cakes. You don\'t know why.',
  'A woman\'s voice: "Are you okay?" You don\'t know who she is.',
  'You see Baltimore. Rain on concrete. Then red dust covers everything.',
  'A boy\'s face. Screaming. You can\'t tell if it\'s a memory or the present.',
  'You remember a song. You hum it. You don\'t know the words.',
  'For one second, you remember who you are. Then the chemicals wash it away.',
];

const DAYS_PER_YEAR = 5;
const TOTAL_YEARS = 5;
const TOTAL_DAYS = DAYS_PER_YEAR * TOTAL_YEARS;

// ============================================
// HELPER FUNCTIONS (pure, no state dependency)
// ============================================

function rollDamage(damageRange) {
  return damageRange[0] + Math.floor(Math.random() * (damageRange[1] - damageRange[0] + 1));
}

function rollHit(accuracy) {
  return Math.random() * 100 < accuracy;
}

function pickRandom(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function makeBar(current, max, width) {
  const clamped = Math.max(0, Math.min(current, max));
  const filled = Math.round((clamped / max) * width);
  return '█'.repeat(filled) + '░'.repeat(width - filled);
}

function getYear(day) {
  return Math.min(Math.ceil(day / DAYS_PER_YEAR), TOTAL_YEARS);
}

function getDayInYear(day) {
  return ((day - 1) % DAYS_PER_YEAR) + 1;
}

function getHumanityPercent(prosthetics) {
  return Math.max(0, 100 - (prosthetics * 8));
}

function getHpColor(current, max) {
  const ratio = current / max;
  if (ratio > 0.5) return 'success';
  if (ratio > 0.25) return 'event';
  return 'danger';
}

// ============================================
// INITIAL STATE
// ============================================

function createInitialState() {
  return {
    day: 1,
    year: 1,
    phase: 'intro',
    bodyParts: JSON.parse(JSON.stringify(BODY_PARTS)),
    weapons: ['fists', 'combat_knife'],
    weaponAmmo: {}, // Track ammo separately: { pistol: 12, rifle: 30, ... }
    equippedWeapon: 'fists',
    pills: { painkillers: 3, stims: 1, brain_pills: 1, organ_pills: 1 },
    kills: 0,
    deaths: 0,
    prosthetics: 0,
    currentEnemy: null,
    currentLocation: null,
    playerHp: 100,
    maxHp: 100,
    enemyHp: 0,
    enemyMaxHp: 0,
    xp: 0,
    level: 1,
    turnsInCombat: 0,
    stimActive: 0,
    stabilizerActive: 0,
    organRegulatorActive: 0,
    warFlashChance: 5,
    memoryFragments: [],
    alive: true,
    gameOver: false,
  };
}

// ============================================
// COMPONENT
// ============================================

export default function MeatMen() {
  const [gameState, setGameState] = useState(createInitialState);
  const [output, setOutput] = useState([]);
  const [inputEnabled, setInputEnabled] = useState(false);
  const [choices, setChoices] = useState([]);
  const outputEndRef = useRef(null);
  const containerRef = useRef(null);

  // ── REF to always have fresh state in callbacks ──
  const gsRef = useRef(gameState);
  useEffect(() => {
    gsRef.current = gameState;
  }, [gameState]);

  // ── Auto-scroll ──
  useEffect(() => {
    outputEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [output]);

  // ── Initialize ──
  useEffect(() => {
    showIntro();
  }, []);

  // ============================================
  // OUTPUT HELPERS
  // ============================================

  const addOutput = useCallback((text, type = 'normal') => {
    setOutput(prev => [...prev, { text, type, id: Date.now() + Math.random() }]);
  }, []);

  const clearOutput = useCallback(() => {
    setOutput([]);
  }, []);

  const addSpacer = useCallback(() => {
    addOutput('', 'spacer');
  }, [addOutput]);

  const addLines = useCallback((lines) => {
    setOutput(prev => [
      ...prev,
      ...lines.map(l => ({
        text: typeof l === 'string' ? l : l.text,
        type: typeof l === 'string' ? 'normal' : l.type || 'normal',
        id: Date.now() + Math.random(),
      })),
    ]);
  }, []);

  // ============================================
  // HP BAR DISPLAY
  // ============================================

  function showHpBars(playerHp, playerMax, enemyHp, enemyMax) {
    const pHp = Math.max(0, playerHp);
    const eHp = Math.max(0, enemyHp);
    const pBar = makeBar(pHp, playerMax, 20);
    const eBar = makeBar(eHp, enemyMax, 20);

    addOutput(`YOU:   [${pBar}] ${pHp}/${playerMax}`, getHpColor(pHp, playerMax));
    addOutput(`ENEMY: [${eBar}] ${eHp}/${enemyMax}`, eHp > enemyMax * 0.5 ? 'danger' : 'event');
  }

  // ============================================
  // GAME PHASES
  // ============================================

  function showIntro() {
    clearOutput();
    setChoices([]);

    addLines([
      { text: '╔═══════════════════════════════════════════════════╗', type: 'header' },
      { text: '║            M  E  A  T     M  E  N                 ║', type: 'header' },
      { text: '║                                                   ║', type: 'header' },
      { text: '║           Help Manu Survive the War               ║', type: 'header' },
      { text: '╚═══════════════════════════════════════════════════╝', type: 'header' },
      { text: '', type: 'spacer' },
      { text: 'AETHON DEFENSE SYSTEMS — MARS THEATER DEPLOYMENT', type: 'dim' },
      { text: 'CLASSIFICATION: MEAT — INFANTRY UNIT', type: 'dim' },
      { text: '', type: 'spacer' },
      { text: 'Before the Engineers sent you to Mars, they shut off your memories.', type: 'story' },
      { text: '', type: 'spacer' },
      { text: 'Then they pumped you full of adrenaline and painkillers and dropped you in the middle of the battlefield.', type: 'story' },
      { text: '', type: 'spacer' },
      { text: 'If you die, the drones drag your body back to base where the doctors do everything they can to bring you back to life. They replace your organs and limbs with prosthetics. Refill your veins with synthetic blood.', type: 'story' },
      { text: '', type: 'spacer' },
      { text: 'If you survive five years of this, you go home.', type: 'story' },
      { text: '', type: 'spacer' },
      { text: 'Not dead, not alive, but something else.', type: 'danger' },
      { text: '', type: 'spacer' },
      { text: '═══════════════════════════════════════', type: 'dim' },
      { text: '', type: 'spacer' },
      { text: 'SUBJECT: MANU', type: 'system' },
      { text: 'STATUS: MEMORY WIPED', type: 'system' },
      { text: 'DEPLOYMENT: MARS — YEAR 1 OF 5', type: 'system' },
      { text: 'OBJECTIVE: SURVIVE', type: 'danger' },
      { text: '', type: 'spacer' },
      { text: '═══════════════════════════════════════', type: 'dim' },
    ]);

    setTimeout(() => {
      addSpacer();
      addOutput('You open your eyes. Red sky. Red dust. The taste of copper and chemicals.', 'story');
      addSpacer();
      addOutput('You don\'t know your name. You don\'t know where you are. You don\'t know why your arms are covered in scars and your chest hums with something mechanical.', 'story');
      addSpacer();
      addOutput('A voice in your earpiece: "Welcome to Mars, Meat. Your five years start now."', 'examine');
      addSpacer();
      addOutput('You reach for the pills in your pocket. You don\'t know what they do, but something tells you to take them.', 'story');
      addSpacer();
      addOutput('Pills for the pain, pills for your brain, pills to keep your body from going insane.', 'dim');
      addSpacer();

      setGameState(prev => ({ ...prev, phase: 'mission_briefing' }));
      startMissionBriefing();
    }, 500);
  }

  // ── MISSION BRIEFING ──

  function startMissionBriefing() {
    const gs = gsRef.current;
    const location = pickRandom(LOCATIONS);

    addSpacer();
    addOutput('════════════════════════════════════', 'system');
    addOutput(`  YEAR ${getYear(gs.day)} OF 6 — DAY ${getDayInYear(gs.day)} OF ${DAYS_PER_YEAR}`, 'system');
    addOutput(`  MISSION #${gs.day}`, 'system');
    addOutput('════════════════════════════════════', 'system');
    addSpacer();
    addOutput(`LOCATION: ${location.name}`, 'room-name');
    addSpacer();
    addOutput(location.description, 'story');
    addSpacer();

    // Pick and scale enemy
    const enemyTypeId = pickRandom(location.enemyTypes);
    const enemyTemplate = ENEMIES.find(e => e.id === enemyTypeId);
    const baseHp = rollDamage(enemyTemplate.hp);
    const dayProgress = gs.day / TOTAL_DAYS;

    const scaledHp = Math.floor(baseHp * (1 + dayProgress * 0.8));
    const scaledDamage = [
      Math.floor(enemyTemplate.damage[0] * (1 + dayProgress * 0.5)),
      Math.floor(enemyTemplate.damage[1] * (1 + dayProgress * 0.5)),
    ];

    const enemyInstance = {
      ...enemyTemplate,
      damage: scaledDamage,
      accuracy: enemyTemplate.accuracy,
    };

    addOutput(`CONTACT: ${enemyTemplate.name}`, 'danger');
    addOutput(enemyTemplate.description, 'dim');
    addSpacer();
    addOutput(`ENEMY HP: ${scaledHp}`, 'danger');
    addOutput(`YOUR HP: ${gs.playerHp}/${gs.maxHp}`, getHpColor(gs.playerHp, gs.maxHp));
    addSpacer();

    setGameState(prev => ({
      ...prev,
      phase: 'combat',
      currentEnemy: enemyInstance,
      currentLocation: location,
      enemyHp: scaledHp,
      enemyMaxHp: scaledHp,
      turnsInCombat: 0,
    }));

    // Small delay so state settles before building choices
    setTimeout(() => presentCombatChoices(), 50);
  }

  // ── COMBAT CHOICES ──

  function presentCombatChoices() {
    const gs = gsRef.current;

    const weaponChoices = gs.weapons.map(wId => {
      const w = WEAPONS[wId];
      let ammoStr = '';
      if (w.ammo !== Infinity) {
        const currentAmmo = gs.weaponAmmo[wId] ?? w.ammo;
        ammoStr = ` [${currentAmmo} rounds]`;
      }
      return {
        label: `ATTACK — ${w.name}${ammoStr}`,
        action: () => executeCombatAttack(wId),
      };
    });

    const pillChoices = [];
    if (gs.pills.painkillers > 0) {
      pillChoices.push({ label: `USE PAINKILLERS (${gs.pills.painkillers})`, action: () => executeUsePill('painkillers') });
    }
    if (gs.pills.stims > 0) {
      pillChoices.push({ label: `USE COMBAT STIMS (${gs.pills.stims})`, action: () => executeUsePill('stims') });
    }
    if (gs.pills.brain_pills > 0) {
      pillChoices.push({ label: `USE NEURAL STABILIZERS (${gs.pills.brain_pills})`, action: () => executeUsePill('brain_pills') });
    }
    if (gs.pills.organ_pills > 0) {
      pillChoices.push({ label: `USE ORGAN REGULATORS (${gs.pills.organ_pills})`, action: () => executeUsePill('organ_pills') });
    }

    setChoices([
      ...weaponChoices,
      ...pillChoices,
      { label: 'CHECK STATUS', action: () => displayStatus() },
    ]);
    setInputEnabled(true);
  }

  // ── COMBAT ATTACK ──

  function executeCombatAttack(weaponId) {
    setInputEnabled(false);
    setChoices([]);

    const gs = gsRef.current;
    const enemy = gs.currentEnemy;

    if (!enemy) {
      addOutput('ERROR: No enemy found. Advancing to next mission.', 'error');
      setTimeout(() => advanceToLoot(), 300);
      return;
    }

    // Check ammo for ranged weapons
    const weaponDef = WEAPONS[weaponId];
    if (weaponDef.ammo !== Infinity) {
      const currentAmmo = gs.weaponAmmo[weaponId] ?? weaponDef.ammo;
      if (currentAmmo <= 0) {
        addOutput(`${weaponDef.name} is empty! You switch to your fists.`, 'danger');
        weaponId = 'fists';
      }
    }

    const actualWeapon = WEAPONS[weaponId];
    addSpacer();

    // ── Player attacks ──
    let playerDamage = 0;
    if (rollHit(actualWeapon.accuracy)) {
      playerDamage = rollDamage(actualWeapon.damage);

      // Stim bonus
      if (gs.stimActive > 0) {
        playerDamage = Math.floor(playerDamage * 1.25);
      }

      // Prosthetic melee bonus
      if ((weaponId === 'fists' || weaponId === 'combat_knife') && !gs.bodyParts.right_arm.organic) {
        playerDamage = Math.floor(playerDamage * 1.3);
        addOutput('Your synthetic arm drives the blow with inhuman force.', 'dim');
      }

      // Prosthetic eyes accuracy bonus (already hit, so boost damage slightly)
      if (!gs.bodyParts.eyes.organic) {
        playerDamage = Math.floor(playerDamage * 1.1);
      }

      addOutput(`You attack with ${actualWeapon.name} — ${playerDamage} damage!`, 'success');
    } else {
      addOutput(`You attack with ${actualWeapon.name} — MISS!`, 'error');
    }

    // Consume ammo
    let newWeaponAmmo = { ...gs.weaponAmmo };
    if (actualWeapon.ammo !== Infinity && weaponId !== 'fists') {
      const currentAmmo = newWeaponAmmo[weaponId] ?? actualWeapon.ammo;
      newWeaponAmmo[weaponId] = Math.max(0, currentAmmo - 1);
    }

    const newEnemyHp = Math.max(0, gs.enemyHp - playerDamage);

    // ── Check enemy death ──
    if (newEnemyHp <= 0) {
      addSpacer();
      addOutput(pickRandom(enemy.deathMessages), 'story');
      addSpacer();
      addOutput(`${enemy.name} KILLED`, 'danger');
      addOutput(`+${enemy.xp} XP`, 'success');

      const newXp = gs.xp + enemy.xp;
      const newLevel = Math.floor(newXp / 50) + 1;
      const leveledUp = newLevel > gs.level;

      if (leveledUp) {
        addSpacer();
        addOutput(`LEVEL UP! You are now level ${newLevel}.`, 'item-found');
        addOutput('Max HP increased. You feel stronger. You feel less human.', 'dim');
      }

      setGameState(prev => ({
        ...prev,
        enemyHp: 0,
        kills: prev.kills + 1,
        xp: newXp,
        level: newLevel,
        maxHp: leveledUp ? prev.maxHp + 10 : prev.maxHp,
        playerHp: leveledUp ? Math.min(prev.playerHp + 10, prev.maxHp + 10) : prev.playerHp,
        weaponAmmo: newWeaponAmmo,
        phase: 'loot',
      }));

      setTimeout(() => advanceToLoot(), 500);
      return;
    }

    // ── Enemy attacks ──
    addSpacer();
    let enemyDamage = 0;
    let actualPlayerHp = gs.playerHp;
    let newBodyParts = JSON.parse(JSON.stringify(gs.bodyParts));

    if (rollHit(enemy.accuracy)) {
      enemyDamage = rollDamage(enemy.damage);

      // Determine which body part gets hit
      const partKeys = Object.keys(newBodyParts);
      const hitPart = pickRandom(partKeys);
      const part = newBodyParts[hitPart];

      // Prosthetics absorb some damage
      if (!part.organic) {
        enemyDamage = Math.floor(enemyDamage * 0.7);
        addOutput(`${enemy.name} hits your ${PROSTHETIC_NAMES[hitPart]} — ${enemyDamage} damage (prosthetic absorbs 30%)`, 'event');
      } else {
        addOutput(`${enemy.name} hits your ${part.name} — ${enemyDamage} damage!`, 'danger');
      }

      // Leg prosthetic gives dodge chance
      if (!newBodyParts.left_leg.organic || !newBodyParts.right_leg.organic) {
        if (Math.random() < 0.1) {
          addOutput('Your hydraulic legs help you dodge — damage reduced!', 'dim');
          enemyDamage = Math.floor(enemyDamage * 0.5);
        }
      }

      // Damage body part
      const newPartHp = Math.max(0, part.hp - enemyDamage);
      const partDestroyed = newPartHp <= 0 && part.hp > 0;
      newBodyParts[hitPart] = { ...part, hp: newPartHp };

      actualPlayerHp = Math.max(0, gs.playerHp - enemyDamage);

      if (partDestroyed) {
        addOutput(`Your ${part.organic ? part.name : PROSTHETIC_NAMES[hitPart]} is DESTROYED!`, 'death');
        if (hitPart === 'heart' || hitPart === 'head') {
          addSpacer();
          addOutput('CRITICAL ORGAN FAILURE', 'death');
          setGameState(prev => ({
            ...prev,
            playerHp: 0,
            bodyParts: newBodyParts,
            enemyHp: newEnemyHp,
            weaponAmmo: newWeaponAmmo,
          }));
          setTimeout(() => handlePlayerDeath(), 500);
          return;
        }
      }

      // Check player death from HP
      if (actualPlayerHp <= 0) {
        setGameState(prev => ({
          ...prev,
          playerHp: 0,
          bodyParts: newBodyParts,
          enemyHp: newEnemyHp,
          weaponAmmo: newWeaponAmmo,
        }));
        setTimeout(() => handlePlayerDeath(), 500);
        return;
      }

      // Update state
      setGameState(prev => ({
        ...prev,
        playerHp: actualPlayerHp,
        bodyParts: newBodyParts,
        enemyHp: newEnemyHp,
        weaponAmmo: newWeaponAmmo,
        turnsInCombat: prev.turnsInCombat + 1,
        stimActive: Math.max(0, prev.stimActive - 1),
        stabilizerActive: Math.max(0, prev.stabilizerActive - 1),
        organRegulatorActive: Math.max(0, prev.organRegulatorActive - 1),
      }));

      // War flash check
      const flashChance = gs.warFlashChance * (gs.stabilizerActive > 0 ? 0.5 : 1);
      if (gs.turnsInCombat > 2 && Math.random() * 100 < flashChance) {
        setTimeout(() => handleWarFlash(), 800);
        return;
      }
    } else {
      addOutput(`${enemy.name} attacks — MISS!`, 'dim');

      setGameState(prev => ({
        ...prev,
        enemyHp: newEnemyHp,
        weaponAmmo: newWeaponAmmo,
        turnsInCombat: prev.turnsInCombat + 1,
        stimActive: Math.max(0, prev.stimActive - 1),
        stabilizerActive: Math.max(0, prev.stabilizerActive - 1),
        organRegulatorActive: Math.max(0, prev.organRegulatorActive - 1),
      }));
    }

    // Show HP bars
    addSpacer();
    showHpBars(actualPlayerHp, gs.maxHp, newEnemyHp, gs.enemyMaxHp);
    addSpacer();

    setTimeout(() => presentCombatChoices(), 300);
  }

  // ── USE PILL ──

  function executeUsePill(pillType) {
    setInputEnabled(false);
    setChoices([]);

    const gs = gsRef.current;

    if (gs.pills[pillType] <= 0) {
      addOutput('You\'re out of those.', 'error');
      setTimeout(() => presentCombatChoices(), 300);
      return;
    }

    addSpacer();

    // Apply pill effect via functional setState to avoid stale data
    setGameState(prev => {
      const updated = { ...prev };
      updated.pills = { ...prev.pills, [pillType]: prev.pills[pillType] - 1 };

      switch (pillType) {
        case 'painkillers': {
          const healAmount = 20;
          updated.playerHp = Math.min(prev.playerHp + healAmount, prev.maxHp);

          // Heal body parts slightly
          const newParts = {};
          Object.keys(prev.bodyParts).forEach(key => {
            const part = prev.bodyParts[key];
            if (part.hp > 0 && part.hp < BODY_PARTS[key].hp) {
              newParts[key] = { ...part, hp: Math.min(part.hp + 10, BODY_PARTS[key].hp) };
            } else {
              newParts[key] = { ...part };
            }
          });
          updated.bodyParts = newParts;
          break;
        }
        case 'stims':
          updated.stimActive = 3;
          break;
        case 'brain_pills':
          updated.stabilizerActive = 5;
          break;
        case 'organ_pills':
          updated.organRegulatorActive = 5;
          break;
        default:
          break;
      }
      return updated;
    });

    // Output messages (these don't depend on state)
    switch (pillType) {
      case 'painkillers':
        addOutput('You swallow the painkillers. The pain fades. Everything fades a little.', 'story');
        addOutput('+20 HP (capped at max)', 'success');
        break;
      case 'stims':
        addOutput('You jam the stim needle into your neck. The world SHARPENS. Everything moves slower. You move faster.', 'story');
        addOutput('+25% damage for 3 turns', 'success');
        break;
      case 'brain_pills':
        addOutput('You swallow the neural stabilizers. The static in your vision clears. The rage recedes. For now.', 'story');
        addOutput('War flash chance reduced for 5 turns', 'success');
        break;
      case 'organ_pills':
        addOutput('You take the organ regulators. Your prosthetics hum in harmony. The rejection symptoms fade.', 'story');
        addOutput('Prosthetic rejection prevented for 5 turns', 'success');
        break;
      default:
        break;
    }

    addSpacer();

    // Enemy gets a free attack while you pill up
    addOutput('You used your turn taking pills. The enemy attacks—', 'dim');

    // Use a small timeout to ensure state has updated, then read from ref
    setTimeout(() => {
      const freshGs = gsRef.current;
      const enemy = freshGs.currentEnemy;

      if (!enemy) {
        setTimeout(() => presentCombatChoices(), 300);
        return;
      }

      if (rollHit(enemy.accuracy)) {
        const dmg = rollDamage(enemy.damage);
        addOutput(`${enemy.name} hits you for ${dmg} damage!`, 'danger');

        setGameState(prev => {
          const newHp = Math.max(0, prev.playerHp - dmg);
          return { ...prev, playerHp: newHp };
        });

        // Check death after a tick
        setTimeout(() => {
          const afterHit = gsRef.current;
          if (afterHit.playerHp <= 0) {
            handlePlayerDeath();
          } else {
            presentCombatChoices();
          }
        }, 50);
      } else {
        addOutput(`${enemy.name} attacks — MISS!`, 'dim');
        addSpacer();
        setTimeout(() => presentCombatChoices(), 300);
      }
    }, 50);
  }

  // ── STATUS DISPLAY ──

  function displayStatus() {
    setInputEnabled(false);
    setChoices([]);

    const gs = gsRef.current;

    addSpacer();
    addOutput('════════ STATUS ════════', 'system');
    addOutput(`SUBJECT: MANU | LEVEL: ${gs.level} | XP: ${gs.xp}`, 'system');
    addOutput(`YEAR: ${getYear(gs.day)} OF 6 | DAY: ${gs.day} OF ${TOTAL_DAYS}`, 'system');
    addOutput(`HP: ${gs.playerHp}/${gs.maxHp} | KILLS: ${gs.kills} | DEATHS: ${gs.deaths}`, 'system');
    addSpacer();

    addOutput('── BODY ──', 'item-found');
    Object.entries(gs.bodyParts).forEach(([key, part]) => {
      const isOrganic = part.organic;
      const name = isOrganic ? part.name : PROSTHETIC_NAMES[key];
      const bar = makeBar(Math.max(0, part.hp), BODY_PARTS[key].hp, 10);
      const status = part.hp <= 0 ? 'DESTROYED' : `${part.hp}/${BODY_PARTS[key].hp}`;
      const color = part.hp <= 0 ? 'death' : !isOrganic ? 'room-name' : part.hp < 50 ? 'event' : 'normal';
      const tag = isOrganic ? '  [ORGANIC]' : '  [PROSTHETIC]';
      addOutput(`  ${name}: [${bar}] ${status}${tag}`, color);
    });

    addSpacer();
    addOutput('── WEAPONS ──', 'item-found');
    gs.weapons.forEach(wId => {
      const w = WEAPONS[wId];
      let ammoStr = '';
      if (w.ammo !== Infinity) {
        const currentAmmo = gs.weaponAmmo[wId] ?? w.ammo;
        ammoStr = ` | AMMO: ${currentAmmo}/${w.maxAmmo || w.ammo}`;
      }
      addOutput(`  ${w.name} — DMG: ${w.damage[0]}-${w.damage[1]} | ACC: ${w.accuracy}%${ammoStr}`, 'normal');
    });

    addSpacer();
    addOutput('── PILLS ──', 'item-found');
    Object.entries(gs.pills).forEach(([key, count]) => {
      const pill = PILLS[key];
      addOutput(`  ${pill.icon} ${pill.name}: ${count} — ${pill.effect}`, count > 0 ? 'normal' : 'dim');
    });

    addSpacer();

    // Active effects
    if (gs.stimActive > 0 || gs.stabilizerActive > 0 || gs.organRegulatorActive > 0) {
      addOutput('── ACTIVE EFFECTS ──', 'event');
      if (gs.stimActive > 0) addOutput(`  💉 COMBAT STIMS: ${gs.stimActive} turns remaining`, 'event');
      if (gs.stabilizerActive > 0) addOutput(`  🧠 NEURAL STABILIZERS: ${gs.stabilizerActive} turns remaining`, 'event');
      if (gs.organRegulatorActive > 0) addOutput(`  ⚙️ ORGAN REGULATORS: ${gs.organRegulatorActive} turns remaining`, 'event');
      addSpacer();
    }

    addOutput(`PROSTHETIC REPLACEMENTS: ${gs.prosthetics}`, gs.prosthetics > 0 ? 'room-name' : 'dim');
    const humanity = getHumanityPercent(gs.prosthetics);
    addOutput(`HUMANITY REMAINING: ${humanity}%`, humanity < 30 ? 'danger' : humanity < 60 ? 'event' : 'normal');
    addOutput(`WAR FLASH RISK: ${gs.warFlashChance}%`, gs.warFlashChance > 15 ? 'danger' : 'dim');
    addSpacer();

    setTimeout(() => presentCombatChoices(), 300);
  }

  // ── WAR FLASH ──

  function handleWarFlash() {
    setInputEnabled(false);
    setChoices([]);

    const gs = gsRef.current;
    const enemy = gs.currentEnemy;

    if (!enemy) {
      setTimeout(() => advanceToLoot(), 300);
      return;
    }

    const flashMsg = pickRandom(WAR_FLASH_MESSAGES);

    addSpacer();
    addOutput('██████████████████████████████████████', 'danger');
    addOutput('         W A R   F L A S H', 'death');
    addOutput('██████████████████████████████████████', 'danger');
    addSpacer();
    addOutput(flashMsg, 'danger');
    addSpacer();

    const selfDamage = 10 + Math.floor(Math.random() * 20);
    addOutput('When the static clears, the enemy is dead. You don\'t remember killing them.', 'story');
    addOutput('Your hands are covered in blood. Your knuckles are broken. Or they would be, if they were still made of bone.', 'dim');
    addSpacer();
    addOutput(`You took ${selfDamage} damage from the flash.`, 'danger');

    addOutput(pickRandom(enemy.deathMessages), 'dim');

    // Memory fragment
    const fragment = pickRandom(MEMORY_FRAGMENTS);
    addSpacer();
    addOutput(`MEMORY FRAGMENT: ${fragment}`, 'event');

    setGameState(prev => ({
      ...prev,
      playerHp: Math.max(1, prev.playerHp - selfDamage),
      enemyHp: 0,
      kills: prev.kills + 1,
      xp: prev.xp + Math.floor(enemy.xp * 0.5),
      warFlashChance: Math.min(prev.warFlashChance + 1, 25),
      memoryFragments: [...prev.memoryFragments, fragment],
      phase: 'loot',
    }));

    addSpacer();
    setTimeout(() => advanceToLoot(), 800);
  }

  // ── LOOT PHASE ──

  function advanceToLoot() {
    const gs = gsRef.current;
    const location = gs.currentLocation;

    if (location && Math.random() < location.lootChance) {
      addSpacer();
      addOutput('── SALVAGE ──', 'item-found');

      const lootTable = [
        { type: 'pill', pill: 'painkillers', count: 1, msg: 'You find a pack of PAINKILLERS on the body.' },
        { type: 'pill', pill: 'painkillers', count: 2, msg: 'Medical supply crate — 2x PAINKILLERS.' },
        { type: 'pill', pill: 'stims', count: 1, msg: 'You find a COMBAT STIM in a pouch.' },
        { type: 'pill', pill: 'brain_pills', count: 1, msg: 'NEURAL STABILIZERS — someone won\'t be needing these anymore.' },
        { type: 'pill', pill: 'organ_pills', count: 1, msg: 'ORGAN REGULATORS. Your prosthetics hum approvingly.' },
        { type: 'weapon', weapon: 'pistol', msg: 'You find a SIDEARM. Still loaded.' },
        { type: 'weapon', weapon: 'rifle', msg: 'An ASSAULT RIFLE, half-buried in the dust.' },
        { type: 'weapon', weapon: 'shotgun', msg: 'A SHOTGUN. The previous owner doesn\'t need it anymore.' },
        { type: 'weapon', weapon: 'grenade', msg: 'FRAG GRENADES. Handle with care.' },
        { type: 'ammo', msg: 'You find an ammunition crate. You restock what you can.' },
      ];

      const loot = pickRandom(lootTable);
      addOutput(loot.msg, 'item-found');

      if (loot.type === 'pill') {
        setGameState(prev => ({
          ...prev,
          pills: { ...prev.pills, [loot.pill]: prev.pills[loot.pill] + loot.count },
        }));
      } else if (loot.type === 'weapon') {
        if (!gs.weapons.includes(loot.weapon)) {
          setGameState(prev => ({
            ...prev,
            weapons: [...prev.weapons, loot.weapon],
            weaponAmmo: { ...prev.weaponAmmo, [loot.weapon]: WEAPONS[loot.weapon].ammo },
          }));
          addOutput(`${WEAPONS[loot.weapon].name} added to your arsenal.`, 'success');
        } else {
          // Refill ammo instead
          const maxAmmo = WEAPONS[loot.weapon].maxAmmo || WEAPONS[loot.weapon].ammo;
          setGameState(prev => ({
            ...prev,
            weaponAmmo: { ...prev.weaponAmmo, [loot.weapon]: maxAmmo },
          }));
          addOutput(`You already have one. Ammo restocked to ${maxAmmo}.`, 'dim');
        }
      } else if (loot.type === 'ammo') {
        // Refill all ranged weapon ammo
        setGameState(prev => {
          const newAmmo = { ...prev.weaponAmmo };
          prev.weapons.forEach(wId => {
            const w = WEAPONS[wId];
            if (w.ammo !== Infinity) {
              const max = w.maxAmmo || w.ammo;
              newAmmo[wId] = max;
            }
          });
          return { ...prev, weaponAmmo: newAmmo };
        });
        addOutput('All ranged weapons restocked.', 'success');
      }
    }

    addSpacer();

    // Advance day
    const newDay = gs.day + 1;

    if (newDay > TOTAL_DAYS) {
      setTimeout(() => showVictory(), 500);
      return;
    }

    // Year milestone
    if (newDay % DAYS_PER_YEAR === 1 && newDay > 1) {
      const completedYear = getYear(newDay) - 1;
      const remaining = TOTAL_YEARS - completedYear;
      addOutput('════════════════════════════════════', 'ending');
      addOutput(`  YEAR ${completedYear} COMPLETE. ${remaining} YEAR${remaining !== 1 ? 'S' : ''} REMAINING.`, 'ending');
      addOutput('════════════════════════════════════', 'ending');
      addSpacer();
      addOutput(`Kills: ${gs.kills} | Deaths: ${gs.deaths} | Prosthetics: ${gs.prosthetics}`, 'dim');
      const humanity = getHumanityPercent(gs.prosthetics);
      addOutput(`Humanity remaining: ${humanity}%`, humanity < 30 ? 'danger' : 'dim');
      addSpacer();

      // Year-end bonus: small heal and pill restock
      addOutput('The Engineers issue fresh supplies between deployments.', 'dim');
      setGameState(prev => ({
        ...prev,
        pills: {
          painkillers: prev.pills.painkillers + 2,
          stims: prev.pills.stims + 1,
          brain_pills: prev.pills.brain_pills + 1,
          organ_pills: prev.pills.organ_pills + 1,
        },
        playerHp: Math.min(prev.playerHp + 20, prev.maxHp),
      }));
      addOutput('+2 Painkillers, +1 Stims, +1 Neural Stabilizers, +1 Organ Regulators', 'success');
      addOutput('+20 HP', 'success');
      addSpacer();
    }

    setGameState(prev => ({ ...prev, day: newDay, phase: 'between_missions' }));

    // Between missions flavor
    addOutput(pickRandom(BETWEEN_MISSION_MESSAGES), 'dim');
    addSpacer();

    // Prosthetic rejection event (random chance if you have prosthetics and no regulators)
    setTimeout(() => {
      const freshGs = gsRef.current;
      if (freshGs.prosthetics > 0 && freshGs.organRegulatorActive <= 0 && Math.random() < 0.15) {
        addOutput('Your body rejects one of its prosthetics. Pain shoots through you like electricity.', 'danger');
        const rejectionDmg = 5 + freshGs.prosthetics * 2;
        addOutput(`-${rejectionDmg} HP from prosthetic rejection.`, 'danger');
        setGameState(prev => ({
          ...prev,
          playerHp: Math.max(1, prev.playerHp - rejectionDmg),
        }));
        addOutput('You should take your organ regulators.', 'dim');
        addSpacer();
      }

      setChoices([
        {
          label: 'NEXT MISSION',
          action: () => {
            setChoices([]);
            startMissionBriefing();
          },
        },
        { label: 'CHECK STATUS', action: () => displayStatus() },
      ]);
      setInputEnabled(true);
    }, 100);
  }

  // ── PLAYER DEATH ──

  function handlePlayerDeath() {
    setInputEnabled(false);
    setChoices([]);

    const gs = gsRef.current;

    addSpacer();
    addOutput('██████████████████████████████████████', 'death');
    addOutput('', 'spacer');
    addOutput('You die.', 'death');
    addOutput('', 'spacer');
    addOutput('Not for long.', 'death');
    addOutput('', 'spacer');
    addOutput('██████████████████████████████████████', 'death');
    addSpacer();

    addOutput(pickRandom(FIELD_MEDIC_MESSAGES), 'story');
    addSpacer();

    // Determine what gets replaced
    const destroyedParts = Object.entries(gs.bodyParts).filter(([, part]) => part.hp <= 0);
    const damagedParts = Object.entries(gs.bodyParts)
      .filter(([, part]) => part.hp > 0 && part.hp < 40 && part.organic)
      .sort((a, b) => a[1].hp - b[1].hp);

    const partsToReplace = [...destroyedParts];

    // Also replace severely damaged organic parts sometimes
    if (damagedParts.length > 0 && Math.random() < 0.5) {
      partsToReplace.push(damagedParts[0]);
    }

    const newBodyParts = JSON.parse(JSON.stringify(gs.bodyParts));
    let newProsthetics = gs.prosthetics;

    if (partsToReplace.length > 0) {
      addOutput('── SURGICAL REPORT ──', 'system');
      const alreadyReplaced = new Set();

      partsToReplace.forEach(([key, part]) => {
        if (alreadyReplaced.has(key)) return;
        alreadyReplaced.add(key);

        if (part.organic || part.hp <= 0) {
          newBodyParts[key] = {
            ...BODY_PARTS[key],
            name: PROSTHETIC_NAMES[key],
            organic: false,
            hp: BODY_PARTS[key].hp,
          };
          newProsthetics++;
          addOutput(`  ${BODY_PARTS[key].name} → ${PROSTHETIC_NAMES[key]}`, 'room-name');
        }
      });
      addSpacer();
    } else {
      addOutput('They patch you up. Synthetic blood, chemical sutures, electrical tape on the soul.', 'dim');
      addSpacer();
    }

    // Heal all parts
    Object.keys(newBodyParts).forEach(key => {
      newBodyParts[key] = { ...newBodyParts[key], hp: BODY_PARTS[key].hp };
    });

    const humanity = getHumanityPercent(newProsthetics);
    addOutput(`HUMANITY REMAINING: ${humanity}%`, humanity < 30 ? 'danger' : 'dim');
    if (humanity < 30) {
      addOutput('You look at your hands. You don\'t recognize them anymore.', 'dim');
    } else if (humanity < 60) {
      addOutput('More machine than man. But the machines keep you alive.', 'dim');
    }
    addSpacer();

    addOutput('The doctors pump you full of chemicals. They wipe the last few hours from your brain. You don\'t remember dying.', 'story');
    addOutput('You don\'t remember anything.', 'dim');
    addSpacer();

    // Refill some ammo on death (you get re-equipped)
    const newAmmo = { ...gs.weaponAmmo };
    gs.weapons.forEach(wId => {
      const w = WEAPONS[wId];
      if (w.ammo !== Infinity) {
        newAmmo[wId] = w.maxAmmo || w.ammo;
      }
    });

    const newDay = gs.day + 1;

    setGameState(prev => ({
      ...prev,
      playerHp: prev.maxHp,
      bodyParts: newBodyParts,
      prosthetics: newProsthetics,
      deaths: prev.deaths + 1,
      alive: true,
      phase: 'between_missions',
      day: newDay,
      weaponAmmo: newAmmo,
      // War flash chance increases with each death
      warFlashChance: Math.min(prev.warFlashChance + 2, 30),
    }));

    // Check if out of years
    if (newDay > TOTAL_DAYS) {
      setTimeout(() => showVictory(), 500);
      return;
    }

    setChoices([
      {
        label: 'WAKE UP. KEEP GOING.',
        action: () => {
          setChoices([]);
          addOutput('You open your eyes. Red sky. Red dust. Another day.', 'story');
          addSpacer();
          startMissionBriefing();
        },
      },
    ]);
    setInputEnabled(true);
  }

  // ── VICTORY ──

  function showVictory() {
    setInputEnabled(false);
    setChoices([]);

    const gs = gsRef.current;
    const humanity = getHumanityPercent(gs.prosthetics);

    addSpacer();
    addOutput('═══════════════════════════════════════', 'ending');
    addOutput('', 'spacer');
    addOutput('Five years.', 'story');
    addOutput('', 'spacer');
    addOutput('The transport ship descends through Earth\'s atmosphere. Through the window, you see blue sky for the first time in five years. You don\'t remember the last time you saw it, but something in your chest — the part that\'s still organic — aches.', 'story');
    addOutput('', 'spacer');
    addOutput('They line you up at the re-adjustment center. A doctor shines a light in your eyes — your real eye and your prosthetic one. He checks your vitals. He reads your file.', 'story');
    addOutput('', 'spacer');
    addOutput('"Welcome home, soldier," he says. He does not smile.', 'story');
    addOutput('', 'spacer');

    if (humanity > 60) {
      addOutput('You still look mostly human. The scars are bad, but the prosthetics are minimal. People might not stare.', 'story');
      addOutput('', 'spacer');
      addOutput('Might.', 'dim');
    } else if (humanity > 30) {
      addOutput('You catch your reflection in the window. Half your face is synthetic. Your arms are metal and wire. You are more machine than man.', 'story');
      addOutput('', 'spacer');
      addOutput('But you\'re alive. Whatever that means now.', 'dim');
    } else {
      addOutput('You don\'t look in mirrors anymore. There\'s no point. The thing that looks back isn\'t you. It\'s what they built from your remains.', 'story');
      addOutput('', 'spacer');
      addOutput('Horribly disfigured. That\'s what they\'ll say. That\'s what they\'ll think when they look away.', 'danger');
    }

    addOutput('', 'spacer');
    addOutput('They give you your pills. Pills for the pain, pills for your brain, pills to keep your body from going insane.', 'story');
    addOutput('', 'spacer');
    addOutput('They give you an address. Your mother\'s apartment. You don\'t remember her, but they say she\'s been waiting.', 'story');
    addOutput('', 'spacer');
    addOutput('You take the train. People look at you and look away. You keep smiling at them. They never smile back.', 'story');
    addOutput('', 'spacer');
    addOutput('The door opens. A woman stands there. She\'s older than you expected. She\'s crying.', 'story');
    addOutput('', 'spacer');
    addOutput('"Manu," she says. "You\'re home."', 'story');
    addOutput('', 'spacer');
    addOutput('You don\'t remember her. But something in your chest — the part that\'s still yours — tells you to hold her.', 'story');
    addOutput('', 'spacer');
    addOutput('So you do.', 'story');
    addOutput('', 'spacer');

    // Death-count specific endings
    if (gs.deaths === 0) {
      addOutput('You survived five years without dying once. The doctors are amazed. The Engineers take notes. You are a statistical anomaly.', 'event');
      addOutput('', 'spacer');
      addOutput('You don\'t feel special. You feel lucky. And luck runs out.', 'dim');
      addOutput('', 'spacer');
    } else if (gs.deaths > 10) {
      addOutput(`You died ${gs.deaths} times. Each time they dragged you back. Each time they replaced another piece of you. Each time you woke up with less of yourself and more of their machines.`, 'event');
      addOutput('', 'spacer');
      addOutput('You wonder how many times a person can die before they stop being a person.', 'dim');
      addOutput('', 'spacer');
    }

    // Memory fragments
    if (gs.memoryFragments.length > 0) {
      addOutput('── RECOVERED MEMORY FRAGMENTS ──', 'event');
      addSpacer();

      // Deduplicate fragments
      const uniqueFragments = [...new Set(gs.memoryFragments)];
      uniqueFragments.forEach(f => {
        addOutput(`  • ${f}`, 'event');
      });
      addOutput('', 'spacer');

      if (uniqueFragments.length >= 5) {
        addOutput('The fragments don\'t form a complete picture. But they form enough of one. You were someone before this. Someone with a life, with people who loved them.', 'story');
        addOutput('', 'spacer');
        addOutput('You can\'t get that person back. But maybe you can build someone new.', 'dim');
        addOutput('', 'spacer');
      } else {
        addOutput('The memories are scattered. Incomplete. Like looking at your life through broken glass.', 'dim');
        addOutput('', 'spacer');
      }
    } else {
      addOutput('You recovered no memory fragments. The person you were before Mars is gone completely.', 'dim');
      addOutput('', 'spacer');
      addOutput('You are a stranger in your own life. But you are alive. That has to count for something.', 'dim');
      addOutput('', 'spacer');
    }

    // Final stats
    addOutput('════════════════════════════════════', 'ending');
    addOutput('  S U R V I V O R', 'ending');
    addOutput('════════════════════════════════════', 'ending');
    addOutput('', 'spacer');
    addOutput(`MISSIONS COMPLETED: ${gs.day}`, 'dim');
    addOutput(`KILLS: ${gs.kills}`, 'dim');
    addOutput(`DEATHS: ${gs.deaths}`, 'dim');
    addOutput(`PROSTHETIC REPLACEMENTS: ${gs.prosthetics}`, 'dim');
    addOutput(`HUMANITY REMAINING: ${humanity}%`, humanity < 30 ? 'danger' : 'dim');
    addOutput(`MEMORY FRAGMENTS: ${gs.memoryFragments.length}`, 'dim');
    addOutput(`LEVEL: ${gs.level}`, 'dim');
    addOutput(`WAR FLASH RISK: ${gs.warFlashChance}%`, gs.warFlashChance > 15 ? 'danger' : 'dim');
    addOutput('', 'spacer');

    // Rating
    const rating = calculateRating(gs, humanity);
    addOutput(`ASSESSMENT: ${rating.title}`, rating.color);
    addOutput(rating.description, 'dim');
    addOutput('', 'spacer');

    addOutput('Not dead, not alive, but something else.', 'dim');
    addOutput('', 'spacer');
    addOutput('════════════════════════════════════', 'ending');

    setGameState(prev => ({ ...prev, gameOver: true, phase: 'victory' }));

    setTimeout(() => {
      setChoices([
        {
          label: 'RESTART — DEPLOY AGAIN',
          action: () => {
            setGameState(createInitialState());
            showIntro();
          },
        },
      ]);
      setInputEnabled(true);
    }, 2000);
  }

  // ── RATING CALCULATION ──

  function calculateRating(gs, humanity) {
    // Score based on multiple factors
    let score = 0;

    // Fewer deaths = better
    if (gs.deaths === 0) score += 40;
    else if (gs.deaths <= 3) score += 30;
    else if (gs.deaths <= 6) score += 20;
    else if (gs.deaths <= 10) score += 10;

    // More humanity = better
    if (humanity > 80) score += 30;
    else if (humanity > 60) score += 25;
    else if (humanity > 40) score += 15;
    else if (humanity > 20) score += 5;

    // Memory fragments
    const uniqueFragments = new Set(gs.memoryFragments).size;
    if (uniqueFragments >= 6) score += 20;
    else if (uniqueFragments >= 4) score += 15;
    else if (uniqueFragments >= 2) score += 10;
    else if (uniqueFragments >= 1) score += 5;

    // Kill efficiency
    const killRatio = gs.kills / gs.day;
    if (killRatio > 0.9) score += 10;
    else if (killRatio > 0.7) score += 5;

    if (score >= 80) {
      return {
        title: 'GHOST — You walked through hell and came back almost whole.',
        color: 'item-found',
        description: 'The Engineers have no explanation. You shouldn\'t have survived this intact. But here you are.',
      };
    } else if (score >= 60) {
      return {
        title: 'SURVIVOR — Scarred but standing.',
        color: 'success',
        description: 'You made it. Not in one piece, but in enough pieces to call it living.',
      };
    } else if (score >= 40) {
      return {
        title: 'REMNANT — More machine than man.',
        color: 'event',
        description: 'They rebuilt you so many times they lost count. The question isn\'t who you are — it\'s what you are.',
      };
    } else if (score >= 20) {
      return {
        title: 'WRAITH — A ghost in a metal shell.',
        color: 'danger',
        description: 'The person who went to Mars is dead. What came back is something else entirely.',
      };
    } else {
      return {
        title: 'MEAT — Just like they always said.',
        color: 'death',
        description: 'You survived. Technically. The doctors aren\'t sure how much of the original is left. Neither are you.',
      };
    }
  }

  // ============================================
  // RENDER
  // ============================================

  const typeColors = {
    'header': '#ff0040',
    'room-name': '#00e5ff',
    'room-desc': '#cccccc',
    'command': '#00ff41',
    'normal': '#cccccc',
    'dim': '#555555',
    'error': '#ff0040',
    'success': '#00ff41',
    'danger': '#ff0040',
    'death': '#ff0040',
    'event': '#ffb000',
    'examine': '#cccccc',
    'help': '#ffb000',
    'item-notice': '#00e5ff',
    'item-found': '#00ff41',
    'story': '#cccccc',
    'journal': '#ffb000',
    'ending': '#8b00ff',
    'system': '#00e5ff',
    'spacer': 'transparent',
  };

  const humanity = getHumanityPercent(gameState.prosthetics);

  return (
    <div style={{ padding: '10px 0' }}>
      {/* Game output */}
      <div
        ref={containerRef}
        style={{
          border: '1px solid #333',
          background: 'rgba(0, 0, 0, 0.5)',
          padding: '15px 20px',
          minHeight: '500px',
          maxHeight: '600px',
          overflowY: 'auto',
          marginBottom: '10px',
          fontFamily: "'VT323', 'Courier New', monospace",
        }}
      >
        {output.map((line) => (
          <div
            key={line.id}
            style={{
              color: typeColors[line.type] || '#cccccc',
              fontSize: line.type === 'header' || line.type === 'ending' ? '1.1rem' : '1rem',
              lineHeight: '1.5',
              minHeight: line.type === 'spacer' ? '0.8em' : 'auto',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              fontWeight: line.type === 'death' || line.type === 'danger' ? 'bold' : 'normal',
              textShadow: line.type === 'death' ? '0 0 10px rgba(255, 0, 64, 0.5)' : 'none',
              fontStyle: line.type === 'dim' ? 'italic' : 'normal',
            }}
          >
            {line.text}
          </div>
        ))}
        <div ref={outputEndRef} />
      </div>

      {/* Choice buttons */}
      {choices.length > 0 && (
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '8px',
          marginBottom: '10px',
        }}>
          {choices.map((choice, i) => (
            <button
              key={i}
              className="bbs-button"
              disabled={!inputEnabled}
              onClick={() => {
                if (inputEnabled) {
                  setInputEnabled(false);
                  choice.action();
                }
              }}
              style={{
                padding: '8px 16px',
                fontSize: '0.9rem',
                fontFamily: "'VT323', 'Courier New', monospace",
                opacity: inputEnabled ? 1 : 0.5,
                cursor: inputEnabled ? 'pointer' : 'not-allowed',
              }}
            >
              {choice.label}
            </button>
          ))}
        </div>
      )}

      {/* Status bar */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '5px',
        padding: '5px 10px',
        border: '1px solid #222',
        fontSize: '0.8rem',
        fontFamily: "'VT323', monospace",
      }}>
        <span style={{ color: '#555' }}>
          YEAR: <span style={{ color: '#00e5ff' }}>
            {getYear(gameState.day)}/6
          </span>
        </span>
        <span style={{ color: '#555' }}>
          DAY: <span style={{ color: '#ffb000' }}>
            {gameState.day}/{TOTAL_DAYS}
          </span>
        </span>
        <span style={{ color: '#555' }}>
          HP: <span style={{
            color: gameState.playerHp > gameState.maxHp * 0.5 ? '#00ff41'
              : gameState.playerHp > gameState.maxHp * 0.25 ? '#ffb000'
              : '#ff0040'
          }}>
            {gameState.playerHp}/{gameState.maxHp}
          </span>
        </span>
        <span style={{ color: '#555' }}>
          KILLS: <span style={{ color: '#ff0040' }}>{gameState.kills}</span>
        </span>
        <span style={{ color: '#555' }}>
          DEATHS: <span style={{ color: '#ff0040' }}>{gameState.deaths}</span>
        </span>
        <span style={{ color: '#555' }}>
          HUMAN: <span style={{
            color: humanity < 30 ? '#ff0040' : humanity < 60 ? '#ffb000' : '#00ff41'
          }}>
            {humanity}%
          </span>
        </span>
        <span style={{ color: '#555' }}>
          LVL: <span style={{ color: '#8b00ff' }}>{gameState.level}</span>
        </span>
      </div>

      {/* Pills quick-reference */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        gap: '15px',
        marginTop: '5px',
        padding: '3px 10px',
        fontSize: '0.75rem',
        fontFamily: "'VT323', monospace",
        color: '#444',
      }}>
        <span>💊 {gameState.pills.painkillers}</span>
        <span>💉 {gameState.pills.stims}</span>
        <span>🧠 {gameState.pills.brain_pills}</span>
        <span>⚙️ {gameState.pills.organ_pills}</span>
      </div>
    </div>
  );
}